import { DataGrid } from "@mui/x-data-grid";
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import SituationalRulesContextMenu from "../../ContextMenu/SituationalRulesContextMenu";
import { cloneDeep, debounce, has, isEmpty } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../../redux/slices/treeSlice";
import {
  FormControl,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import Tree from "rc-tree";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genericRules, validateOnFlow, validateRevertTime, validationTime } from "../../../utils/validate";
import { scrollToSelectedItem } from "../../../utils/utilFunctions";


const validationRules = {
  minLoops: genericRules.numericRule,
  maxLoops: genericRules.numericRule,
  MAX_REPETITION:{...genericRules.numericRule,maxLength:10}
};


const RecordDataValidation = ({ isVisible }) => {
  const { t } = useTranslation();

  // Helper function to safely decode URI component
  const safeDecodeURIComponent = (value) => {
    if (!value) return "";
    const replacedValue = value.replace(/\+/g, ' ');
    
    try {
      const decoded = decodeURIComponent(replacedValue);
      // Check if the string was actually encoded by comparing with original
      return decoded !== replacedValue ? decoded : replacedValue;
    } catch (e) {
      // If decoding fails, return the original string
      return replacedValue;
    }
  };
  const [errors, setErrors] = useState({});
  const [fieldData, setFieldData] = useState([]);
  const [tableRowsData, setTableRowData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [minLoops,setMinLoops] = useState('0');
  const [maxLoops,setMaxLoops] = useState('0');
  const [maxRepetition,setMaxRepetition] = useState('');
  const [isRequired,setIsRequired] = useState(false);
  const [selectedRule,setSelectedRule] = useState([]);
  const [selectedField,setSelectedField] = useState([]);
  const [index,setIndex] = useState(0);
  const [situationalRules,setSituationalRules] = useState ([]);
  const [contextMenuId, setContextMenuId] = useState('')
  const errorRef = useRef();
  const parentRef = useRef();
  const manualClick = useRef(false);
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const rightTree = useSelector((state) => state.treeData[parentScreen].rightTree);
  const currentTab = rightTree.currentTab;
  const tree = rightTree.tree;
  const {selectedNode , translatedData} = tree[currentTab];
  const templateHeader = rightTree.tree.templateHeader;

  const RULE_TYPES = {
    SELECT_VALIDATION_TYPE: t('LBL_SELECT_VALIDATION_TYPE'),
    REQUIRED: t('REQUIRED'),
    CONDITIONAL: t("CONDITIONAL"),
    EXCLUSION: t("EXCLUSION"),
    LIST_CONDITIONAL: t("LIST CONDITIONAL").split(" ").join('_'),
    PAIRING: t("PAIRING"),
  };

  const columns = [
    {
      field: 'checkbox',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedField?.includes(params.row.id) || false}
          onChange={(e) => {
            const isChecked = e.target.checked;
            let newSelection;
            
            if (isChecked) {
              newSelection = [...(selectedField || []), params.row.id];
            } else {
              newSelection = (selectedField || []).filter(id => id !== params.row.id);
            }
            
            onRowsSelectionHandler(newSelection);
          }}
        />
      ),
    },
    { 
      field: t('LBL_FLD_NAME')?.replace?.(':',"") || "Field Name", 
      headerName: t('LBL_FLD_NAME')?.replace?.(':',"") || "Field Name", 
      flex: 1 
    }
  ];

  const ruleTypesDropDownItems = Object.keys(RULE_TYPES).map(function (
    key,
    index
  ) {
    const value = RULE_TYPES[key];
    return (
      <MenuItem key={key + index} value={value}>
        {value}
      </MenuItem>
    );
  });


  // Selected Record Fields getting Function to show in Conditional Field DropDown
  const getSelectedRecordFields = () => {
    
    return fieldData
      .filter(ele => {
        if (['RECORD', 'RECORD_LOOP'].includes(selectedNode?.ICON_DISPLAY)) {
          return ele?.ICON_DISPLAY.includes('FIELD');
        } else {
          if (ele?.ICON_DISPLAY.includes('FIELD_GROUP')) {
            return false;
          } else return ['FIELD', 'FIELD_LOOP'].includes(ele?.ICON_DISPLAY);
        }
      })
      .map((obj, index) => {
        const value = parentScreen === "inbound" ? (obj?.DESCRIPTION || obj?.MAPNAME) : (obj?.MAPNAME || obj?.DESCRIPTION);
        if (value) {
          const displayValue = value.replace(/\+/g, ' ');
          return (
            <MenuItem key={value + index} value={value}>
              {displayValue}
            </MenuItem>
          );
        }
        return null;
      });
  };

  // Is Required Checkbox Handler
  const handleCheckbox = () => {
    const value = !isRequired
    const EDIT_RULE = translateFunction(situationalRules,"isRequired",value);
    setIsRequired(value);
    debounceUpdateRedux(selectedNode.key, EDIT_RULE, 500, translatedData);
  };

  // Field Selection Handler in DataGrid
  const onRowsSelectionHandler = (value) => {
    // Ensure value is always an array
    const safeValue = Array.isArray(value) ? value : [];
    const newSelectedField = []
    
    // Look up selected fields from tableRowsData which has the proper mapping
    safeValue.forEach(selectedId => {
      const tableRow = tableRowsData?.find(row => row.id === selectedId);
      if (tableRow?.originalField) {
        const fieldName = tableRow.originalField?.MAPNAME || tableRow.originalField?.TYPE || tableRow.originalField?.DESCRIPTION;
        if (fieldName) {
          newSelectedField.push(fieldName);
        }
      }
    });
    
    const newArray = cloneDeep(situationalRules);
    newArray[index] ={...newArray[index],selectedField:newSelectedField}
    setSituationalRules(newArray);
    updateFieldRulesArray(newArray);
    dataToRcTreeData(newArray);
    setSelectedField(safeValue);
    // console.log(safeValue,newSelectedField);
  };


  // Already Selected Fields in DataGrid
  const getSelectedFields = useCallback((index, data) => {
    if (!Array.isArray(data) || !Array.isArray(tableRowsData)) {
      setSelectedField([]);
      return;
    }
    
    let selectedKeys = [];
    const currentData = data[index || 0];
    
    if (currentData?.selectedField && Array.isArray(currentData.selectedField)) {
      currentData.selectedField.forEach(selectedFieldName => {
        // Find the matching row in tableRowsData
        const matchingRow = tableRowsData.find(row => {
          const originalField = row.originalField;
          return originalField?.MAPNAME === selectedFieldName.replace(/\+/g, ' ') || 
                 originalField?.TYPE === selectedFieldName.replace(/\+/g, ' ') || 
                 originalField?.DESCRIPTION === selectedFieldName.replace(/\+/g, ' ');
        });
        
        if (matchingRow) {
          selectedKeys.push(matchingRow.id);
        }
      });
    }
    
    setSelectedField(selectedKeys);
  }, [tableRowsData, selectedNode?.key, translatedData]);


  const translateFunction = (newValue,name,value)=>{
    const data = cloneDeep(newValue) || [];
      const EDIT_RULE = data.map(ele=>{
        if(ele.conditionalField){
          ele.selectedField.splice(0,0,ele.conditionalField);
        }
        // Use the actual Record Separator character instead of URL-encoded version
        const FIELD_NAMES = ele?.selectedField?.join('\u001e') || "";
        const FIELD_RULE_TYPE = ele.ruleType === t('LBL_SELECT_VALIDATION_TYPE') ? t('UNKNOWN_RULE_TYPE'): ele.ruleType;
        return {
          FIELD_NAMES,
          FIELD_RULE_TYPE,
          TYPE: "FIELD_RULE"
        }
      })

      const recordRule = {
        MAX_LOOPS: String(name === "maxLoops" ? value : maxLoops),
        MIN_LOOPS: String(name === "minLoops" ? value : minLoops),
        REQUIRED: name === "isRequired" ? String(value) : String(isRequired),
        TYPE: "RECORD_RULE"
      };
      
      if (parentScreen === 'outbound' && 
          templateHeader?.FILETYPE === 'EDI' && 
          templateHeader?.FILETYPE_VERSION !== '4010') {
        recordRule.MAX_REPETITION = String(name === "MAX_REPETITION" ? value : maxRepetition);
      }
      
      EDIT_RULE.push(recordRule);
      
    return EDIT_RULE
  }
 

  // Function to update SituationField Rule Array
  const updateFieldRulesArray = (newArray) => {
    const EDIT_RULE = translateFunction(newArray);
    debounceUpdateRedux(selectedNode.key, EDIT_RULE, 500, translatedData);
  };

  //Function to set Max and Min Loops Fields
  const setMaxAndMinLoops = ({ value, name },stateFunction) => {

    const errorState = validateOnFlow(name, value, validationRules)

    stateFunction(value);
    setErrors((prev) => ({
      ...prev,
      ...errorState
    }))

    if(isEmpty(errorState) || !value){
      const EDIT_RULE = translateFunction(situationalRules,name,value);
      debounceUpdateRedux(selectedNode.key, EDIT_RULE, 500, translatedData);
    }

    if(!isEmpty(errorState)){
      setTimeout(()=>{
        stateFunction((previousPrecisionValue) => {
          let val = previousPrecisionValue;
          val = val.replace(/\D/g, '');
          if(val.length > validationRules[name]?.maxLength){
            val = val.substring(0,validationRules[name]?.maxLength);
          }
          return val;
        })
      },validateRevertTime)
      if(errorRef?.current?.[name]) clearTimeout(errorRef?.current[name]);
      const timerId= setTimeout(() => {
        setErrors((prev) => {
          const copyPrev = JSON.parse(JSON.stringify(prev));
          delete copyPrev[name];
          return copyPrev;
        })
      }, validationTime)
      errorRef.current = {...errorRef.current , [name] : timerId}
      return;
    }
    
    
    
  };

  // Check Max and Min Loops Whether Min loop is less than or equal to Max Loops
  const checkMaximumLoops = () => {
    const min = Number(minLoops);
    const max = Number(maxLoops);

    if(!minLoops && !maxLoops){
      setMinLoops(prev=>'0');
      setMaxLoops(prev=>'0');
    }
    if(!minLoops){
      setMinLoops(prev =>'0');
    }

    if (min >= max) {
      const EDIT_RULE = translateFunction(situationalRules,"maxLoops",minLoops );
      debounceUpdateRedux(selectedNode.key, EDIT_RULE, 500, translatedData,true);
      setMaxLoops(minLoops)
    }
  };

  // Debounce Function Updating
  const submitChange = (key, value, time, data,unsetSelectedNode) => {
    const translatedData = cloneDeep(data);
    eachDeep(translatedData, (obj) => {
      const nodeKey = obj?.key;
      if (nodeKey && nodeKey === key) {
        obj.EDIT_RULE = value;
        if(!unsetSelectedNode){
          dispatch(treeActions.setRightTreeSelectedNode({
            screen:parentScreen,
            type:currentTab,
            data:obj
          }))
        }
        return;
      }
    });
    
    
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: currentTab,
        data: translatedData,
      })
    );
  };


  const debounceUpdateRedux = useCallback(debounce(submitChange, 800), [
    currentTab,
    tree,
  ]);

  
  // Rule Type and Conditional Field 
  const dropDownHandler =({value,name})=>{
    const newArray = [...cloneDeep(situationalRules)];
    const conditionalArray = ["CONDITIONAL","LIST_CONDITIONAL"];

    if(conditionalArray.includes(newArray[index].ruleType) &&
      name=== "ruleType" && !conditionalArray.includes(value)){

      newArray[index].conditionalField ='';

    }

    if(name === 'ruleType'){
      newArray[index] ={
        ...newArray[index],
        ruleType: value
      }
      
    }else{
      newArray[index] ={
        ...newArray[index],
        conditionalField: value
      }
    }
    
    setSituationalRules(newArray)
    updateFieldRulesArray(newArray);
    dataToRcTreeData(newArray);
  }
  
  //Redux Data to Rc Tree
  const dataToRcTreeData = (data) => {
    const rcTreeData = data.map((ele, idx) => {
      return {
        key: `sfr-${idx}`,
        title: ele?.ruleType === t('LBL_SELECT_VALIDATION_TYPE') ? t('TXT_INKNOWN_RULE_TYPE'): ele?.ruleType,
        ICON_DISPLAY: "ITEM_PROPERTIES",
      };
    });
    setTreeData(rcTreeData);
    setSituationalRules(data);
    
  };

  // RC Tree Functions 
  const Icon = (node) => {
    return <span className={"tree-icon " + node.ICON_DISPLAY}></span>;
  };

  const titleRender = (node) => {
    return <span data-key={`${node.key}`}>{node.title}</span>;
  };

  const rightClickHandler =(data)=>{
    setSelectedRule([data.node.key]);
  }

  const selectHandler =(keys,data)=>{
    setSelectedRule([data.node.key]);
  }

  const validateFunction2 = () =>{

    const fieldsArray = selectedNode?.children?.filter(
      (ele) => {
        if(['RECORD','RECORD_LOOP'].includes(selectedNode?.ICON_DISPLAY)){
          return ele?.ICON_DISPLAY?.includes('FIELD'); 
        }
        else {
          if(ele?.ICON_DISPLAY?.includes('FIELD_GROUP')){
            return false;
          }
          else return ['FIELD','FIELD_LOOP']?.includes(ele?.ICON_DISPLAY)
        }
      }
    ) || [];
    setFieldData(fieldsArray);
    const fieldData = fieldsArray?.filter((field) => (field?.FIELDTYPE !== 'CONSTANT' || parentScreen === "outbound"))?.map((obj, index) => {
      
      const fieldKey = obj?.key || `st-field-${index}`;
      return { 
        id: fieldKey, 
        "Field Name": (obj?.MAPNAME || obj?.DESCRIPTION || obj?.TYPE || "UNNAMED").replace(/\+/g, ' '),
        originalField: obj 
      };
    }) || [];

    setTableRowData(fieldData);

    const dataValidationArray = selectedNode?.EDIT_RULE || [];
    const recordRuleObj = dataValidationArray.find(ele=>ele?.TYPE === "RECORD_RULE") || {}
    const situationalFieldRules = dataValidationArray.filter(ele=>ele?.TYPE !== "RECORD_RULE") || []
    setMinLoops(Number(recordRuleObj?.MIN_LOOPS) || 0);
    setMaxLoops(Number(recordRuleObj?.MAX_LOOPS) || 0);
    setMaxRepetition(recordRuleObj?.MAX_REPETITION || 1);
    setIsRequired(recordRuleObj?.REQUIRED === "true" ? true : false);
    const translatedRulesArray = situationalFieldRules.map(ele=>{
      // Handle both URL-encoded and actual Record Separator character for backward compatibility
      let fieldName = [];
      try {
        // First try with actual Record Separator character
        fieldName = ele?.FIELD_NAMES?.split('\u001e') || [];
        // If no split occurred and string contains %1E, try with URL-encoded version
        if (fieldName.length === 1 && ele?.FIELD_NAMES?.includes('%1E')) {
          fieldName = ele?.FIELD_NAMES?.split('%1E') || [];
        }
      } catch (error) {
        console.warn('Error parsing FIELD_NAMES:', error);
        fieldName = [];
      }
      let ruleType = ele?.FIELD_RULE_TYPE || t('LBL_SELECT_VALIDATION_TYPE');
      let conditionalField = (fieldName.length > 0 && ["CONDITIONAL","LIST_CONDITIONAL"].includes(ruleType)) ? fieldName[0] :'';
      let selectedField = ["CONDITIONAL","LIST_CONDITIONAL"].includes(ruleType) ? fieldName.splice(1) : fieldName;
      return {
        ruleType,
        conditionalField,
        selectedField
      }
    })
    // console.log(translatedRulesArray);
    dataToRcTreeData(translatedRulesArray);
    setSelectedRule([]);
    if(translatedRulesArray.length){
      setIndex(0);
      setSelectedRule([`sfr-0`]);
    }
    
  }



  useEffect(() => {
    if (['RECORD','RECORD_LOOP','FIELD_GROUP','REPEATING_FIELD_GROUP'].includes(selectedNode?.ICON_DISPLAY)) {
      validateFunction2();
    }
  }, [selectedNode?.key,currentTab]); 

  useEffect(() => {
    const safeSelectedRule = Array.isArray(selectedRule) ? selectedRule : [];
    let index = 0;
  
    if (safeSelectedRule.length > 0) {
      const parts = safeSelectedRule[0].split("-");
      if (parts.length === 2 && !isNaN(parts[1])) {
        index = Number(parts[1]);
      }
    }
  
    setIndex(index);
    getSelectedFields(index, situationalRules || []);
  }, [translatedData, selectedRule]);
   

  useEffect(()=>{
    if(manualClick.current){
      scrollToSelectedItem(selectedRule,parentRef)
    }
    manualClick.current =false;
  },[selectedRule])

  useEffect(() => {
    setContextMenuId(`${parentScreen}-situationalRules`)
  },[parentScreen])
  
  return (
    <div>
      <Stack spacing={"20px"} className="tp-segment">
        {(templateHeader?.FILETYPE !== 'EDI' || !selectedNode?.ICON_DISPLAY?.includes('FIELD_GROUP')) && <>
            <FormControl className="width-100" size="small">
              <TextField
                variant="outlined"
                size="small"
                autoFocus={true}
                label={t("LBL_MIN_LOOPS")}
                value={minLoops}
                name="minLoops"
                onChange={(e) => setMaxAndMinLoops(e.target,setMinLoops)}
                onBlur={checkMaximumLoops}
                helperText={
                  (has(errors, "minLoops") &&
                    errors["minLoops"]) ||
                  ""
                }
                error={has(errors, "minLoops")}
              />
            </FormControl>
            <FormControl className="width-100" size="small">
              <TextField
                variant="outlined"
                size="small"
                label={t("LBL_MAX_LOOPS")}
                value={maxLoops}
                name="maxLoops"
                onChange={(e) => setMaxAndMinLoops(e.target,setMaxLoops)}
                onBlur={checkMaximumLoops}
                helperText={
                  (has(errors, "maxLoops") &&
                    errors["maxLoops"]) ||
                  ""
                }
                error={has(errors, "maxLoops")}
              />
            </FormControl>
          </>
        }
         {(parentScreen === 'outbound' && templateHeader?.FILETYPE === 'EDI' && templateHeader?.FILETYPE_VERSION !=='4010') && <FormControl>
          <TextField
            variant='outlined'
            size='small'
            autoComplete='off'
            type='text'
            className={'width-100'}
            label={t('LBL_MAX_REPS')}
            name={'MAX_REPETITION'}
            value={maxRepetition}
            onChange={(e)=>{
              setMaxAndMinLoops(e.target,setMaxRepetition)
            }}
            onBlur={(e)=>{
              if(!e.target.value){
                setMaxRepetition(1);
              }
            }}
            helperText={errors?.MAX_REPETITION}
            error={has(errors,'MAX_REPETITION')}
          />
         </FormControl>} 
      
        <FormGroup sx={{ marginTop: "5px !important" }}>
          <TPCheckbox
            handleCheck={handleCheckbox}
            label={['RECORD','RECORD_LOOP'].includes(selectedNode?.ICON_DISPLAY) ? t("BTN_REC_REQ") : t('BTN_FLD_REQ') }
            checked={isRequired}
            inputProps={{ type: "number" }}
          />
        </FormGroup>
      </Stack>

      <FormControl className="width-100" size="small">
        <FormLabel>{t("LBL_SITUATIONAL_FLD_RULES")}</FormLabel>
        <div ref={parentRef} className="div-textbox" id={contextMenuId}>
          <Tree
            titleRender={titleRender}
            selectedKeys={selectedRule}
            treeData={treeData}
            selectable={true}
            icon={Icon}
            onSelect={selectHandler}
            onRightClick={rightClickHandler}
          />
        </div>
        <SituationalRulesContextMenu
          targetId={contextMenuId}
          manualClick={manualClick}
          fieldRulesArray={situationalRules}
          updateFunction={updateFieldRulesArray}
          treeUpdate={dataToRcTreeData}
          setSelectedRule={setSelectedRule}
          selectedRule={selectedRule}
        />
      </FormControl>

      <Stack spacing={"20px"} className="tp-segment">
        <FormControl variant="outlined" className="width-100" size="small">
          <InputLabel id="ruleTypeLabelID">{t("LBL_RULE_TYPE")}</InputLabel>
          <Select
            className={"width-100"}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                },
              },
            }}
            name={"ruleType"}
            value={situationalRules[index]?.ruleType || ''}
            labelId="ruleTypeLabelID"
            label={t("LBL_RULE_TYPE")}
            id="ruleTypeDropdown"
            disabled={selectedRule?.length === 0}
            onChange={(e) => dropDownHandler(e.target)}
          >
            {ruleTypesDropDownItems}
          </Select>
        </FormControl>
        {(selectedRule?.length > 0 &&
          (situationalRules?.[index]?.ruleType === RULE_TYPES.CONDITIONAL ||
          situationalRules?.[index]?.ruleType ===
            RULE_TYPES.LIST_CONDITIONAL)) && (
          <FormControl variant="outlined" className="width-100" size="small">
            <InputLabel id="conditionalFieldLabel">
              {t("LBL_COND_FLD")}
            </InputLabel>
            <Select
              className={"width-100"}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              name={"conditionalField"}
              value={safeDecodeURIComponent(situationalRules?.[index]?.conditionalField)}
              labelId="conditionalFieldLabel"
              label={t("LBL_COND_FLD")}
              id="conditionalFieldDropDown"
              onChange={(e) => dropDownHandler(e.target)}>
              {getSelectedRecordFields()}
            </Select>
          </FormControl>
        )}
        {treeData?.length && selectedRule?.length > 0 ? (
          <div
            style={{
              height: tableRowsData?.length > 5 ? "400px" : "250px",
              width: "100%",
            }}
          >
            {isVisible && Array.isArray(tableRowsData) && tableRowsData?.length > 0 && (
              <DataGrid
                className="customGrid"
                rows={tableRowsData}
                rowHeight={25}
                columns={columns}
                sx={styledDataGridOption}
                hideFooter={tableRowsData?.length <= 5}
                hideFooterSelectedRowCount={true}
                getRowId={(row) => row?.id}
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                checkboxSelection={false}
              />
            )}
          </div>
        ) : (
          ""
        )}
      </Stack>
    </div>
  );
};

export default RecordDataValidation;
const styledDataGridOption = {
  "& .MuiDataGrid-cell:focus": {
    outline: " none",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "transparent !important",
  },
  "& .Mui-checked": {
    color: "#005FB8",
  },
  "& .MuiDataGrid-cell": {
    outline: "none !important",
    borderRight: `1px solid #d9d9d9`,
  },
  "& .MuiDataGrid-columnHeader": {
    outline: "none !important",
  },
  "& .MuiDataGrid-root": {
    maxHeight:'400px !important',
  },
};

